/** Запросы к боту.
 *
 * Все обращения идут одним путём: POST с подписью Telegram в заголовке.
 * Подпись — единственное доказательство, что запрос от того, за кого себя
 * выдаёт клиент, поэтому забыть её нельзя ни в одном месте — отсюда общий
 * `request`, а не разрозненные fetch по экранам.
 */
import { initData } from './tg';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Тело ответа сервера: коды отказов корзины живут здесь */
    readonly body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parse(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

export async function request<T>(path: string, body: unknown = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Init-Data': initData(),
      },
      body: JSON.stringify(body),
    });
  } catch (error) {
    // Домашний канал сервера часто отваливается — говорим об этом словами,
    // а не «TypeError: Failed to fetch»
    throw new ApiError('нет связи с сервером', 0);
  }

  const data = (await parse(response)) as { error?: string };
  if (!response.ok) {
    throw new ApiError(data.error ?? String(response.status), response.status,
                       data as Record<string, unknown>);
  }
  return data as T;
}

/** Загрузка файлов: подпись едет полем формы — заголовки тут занял браузер. */
export async function upload<T>(
  path: string,
  fields: Record<string, string>,
  files: File[],
): Promise<T> {
  const form = new FormData();
  form.append('initData', initData());
  for (const [key, value] of Object.entries(fields)) form.append(key, value);
  for (const file of files) form.append('file', file, file.name);

  // Подпись и в заголовке: сервер проверяет её до чтения формы
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'X-Init-Data': initData() },
    body: form,
  });
  const data = (await parse(response)) as { error?: string };
  if (!response.ok) {
    throw new ApiError(data.error ?? String(response.status), response.status);
  }
  return data as T;
}
